import React from 'react';

var scriptsSections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    body: React.createElement('div', null,
      React.createElement('p', null, 'Scripts let you add custom behavior to your slates using TypeScript. Each script runs in a sandboxed environment when a slate is loaded.'),
      React.createElement('p', null, 'To create a script, click ', React.createElement('strong', null, 'File'), ' in the toolbar and select ', React.createElement('strong', null, 'New Script'), '. Every script starts with a default ', React.createElement('code', null, 'onSlateLoaded'), ' function that you can customize.'),
      React.createElement('h4', null, 'Quick Example'),
      React.createElement('pre', null,
        React.createElement('code', null, 'function onSlateLoaded(ctx: SlateContext) {\n  let status = ctx.slate.fields[\'Status\'];\n\n  if (status.value === \'Closed\') {\n    ctx.slate.fields[\'Notes\'].hide();\n  }\n}')
      )
    ),
  },
  {
    id: 'concepts',
    title: 'Concepts',
    children: [
      {
        id: 'script-structure',
        title: 'Script Structure',
        body: React.createElement('div', null,
          React.createElement('p', null, 'Every script needs a function called ', React.createElement('code', null, 'onSlateLoaded'), '. This is the entry point that runs when a slate containing the script is opened.'),
          React.createElement('h4', null, 'Basic Template'),
          React.createElement('pre', null,
            React.createElement('code', null, 'function onSlateLoaded(ctx: SlateContext) {\n  // your code here\n}')
          ),
          React.createElement('p', null, 'The ', React.createElement('code', null, 'ctx'), ' parameter is a ', React.createElement('code', null, 'SlateContext'), ' object. Its main property is ', React.createElement('code', null, 'ctx.slate'), ', which gives you access to the slate\'s fields. This function is called once each time the slate loads.')
        ),
      },
      {
        id: 'slate-context',
        title: 'Slate Context',
        body: React.createElement('div', null,
          React.createElement('p', null, 'The ', React.createElement('code', null, 'ctx'), ' object passed to ', React.createElement('code', null, 'onSlateLoaded'), ' contains one property: ', React.createElement('code', null, 'ctx.slate'), '. Through it you access the slate\'s fields.'),
          React.createElement('h4', null, 'Accessing Fields'),
          React.createElement('p', null, 'Fields are accessed by name through ', React.createElement('code', null, 'ctx.slate.fields'), ':'),
          React.createElement('pre', null,
            React.createElement('code', null, 'let name = ctx.slate.fields[\'Full Name\'];\nlet status = ctx.slate.fields[\'Status\'];')
          ),
          React.createElement('h4', null, 'Field Properties'),
          React.createElement('ul', null,
            React.createElement('li', null, React.createElement('code', null, 'field.value'), ' \u2014 The current value of the field'),
            React.createElement('li', null, React.createElement('code', null, 'field.visible'), ' \u2014 Whether the field is currently visible'),
            React.createElement('li', null, React.createElement('code', null, 'field.options'), ' \u2014 The available options (selector fields only)')
          ),
          React.createElement('h4', null, 'Field Methods'),
          React.createElement('ul', null,
            React.createElement('li', null, React.createElement('code', null, 'field.show()'), ' \u2014 Makes the field visible'),
            React.createElement('li', null, React.createElement('code', null, 'field.hide()'), ' \u2014 Hides the field'),
            React.createElement('li', null, React.createElement('code', null, 'field.setOptions([...])'), ' \u2014 Sets available options (selector fields only)'),
            React.createElement('li', null, React.createElement('code', null, 'field.onValueChanged(callback)'), ' \u2014 Runs a callback when the field\'s value changes')
          )
        ),
      },
      {
        id: 'event-handlers',
        title: 'Event Handlers',
        body: React.createElement('div', null,
          React.createElement('p', null, 'Beyond the initial ', React.createElement('code', null, 'onSlateLoaded'), ' call, you can listen for changes on individual fields using ', React.createElement('code', null, 'onValueChanged'), '.'),
          React.createElement('h4', null, 'Reacting to Changes'),
          React.createElement('p', null, 'Call ', React.createElement('code', null, 'field.onValueChanged(callback)'), ' to run code whenever a field\'s value changes. This is useful for showing or hiding fields, updating options, or running validation.'),
          React.createElement('pre', null,
            React.createElement('code', null, 'function onSlateLoaded(ctx: SlateContext) {\n  let type = ctx.slate.fields[\'Type\'];\n  let subtype = ctx.slate.fields[\'Subtype\'];\n\n  type.onValueChanged(() => {\n    if (type.value === \'A\') {\n      subtype.setOptions([\'A1\', \'A2\', \'A3\']);\n      subtype.show();\n    } else {\n      subtype.hide();\n    }\n  });\n}')
          )
        ),
      },
      {
        id: 'testing-scripts',
        title: 'Testing Scripts',
        body: React.createElement('div', null,
          React.createElement('p', null, 'You can test your scripts against real slates without publishing changes. Click the ', React.createElement('strong', null, 'Test'), ' button in the toolbar to open the test dialog.'),
          React.createElement('h4', null, 'How Testing Works'),
          React.createElement('ol', null,
            React.createElement('li', null, 'Select a template to use as a test target'),
            React.createElement('li', null, 'The script is injected into a temporary copy of the template'),
            React.createElement('li', null, 'A preview opens in the slate pane so you can see the result'),
            React.createElement('li', null, 'Changes made during testing do not affect the original template')
          ),
          React.createElement('p', null, 'Use ', React.createElement('strong', null, 'Hot Reload'), ' to re-inject your latest code into the same test slate without reopening the dialog.')
        ),
      },
    ],
  },
];

export default scriptsSections;
